import { useState, useEffect } from 'react';
import { Dialog, Pane, Heading, Text, Button, TrashIcon, IconButton, Badge, TextInput, PlusIcon, toaster } from 'evergreen-ui';
import { overlapsPassiveWindow, parseNotteAttivaIntervalli } from './nottePassiva';
import { normalizeTime } from '../../utils/timeUtils';

export default function TurniDayDetailModal({
  isOpen,
  onClose,
  selectedDay,
  isDirector,
  onRemoveShift,
  onAssignClick,
  onSetActiveNightIntervals,
}) {
  // intervalli locali per shift: { [shiftId]: [{inizio, fine}, ...] }
  const [localIntervals, setLocalIntervals] = useState({});
  // quale shift ha il form "aggiungi emergenza" aperto
  const [addingFor, setAddingFor] = useState(null);
  const [newInizio, setNewInizio] = useState('01:00');
  const [newFine, setNewFine] = useState('05:00');

  useEffect(() => {
    if (!selectedDay) return;
    const init = {};
    (selectedDay.shifts.raw || []).forEach(rs => {
      init[rs.id] = parseNotteAttivaIntervalli(rs.notte_attiva_intervalli);
    });
    setLocalIntervals(init);
    setAddingFor(null);
  }, [selectedDay]);

  const handleAddInterval = (shiftId) => {
    const inizio = normalizeTime(newInizio);
    const fine   = normalizeTime(newFine);
    if (!inizio || !fine || inizio >= fine) {
      toaster.warning('Orario non valido: l\'inizio deve essere prima della fine');
      return;
    }
    const updated = [...(localIntervals[shiftId] || []), { inizio, fine }];
    setLocalIntervals(prev => ({ ...prev, [shiftId]: updated }));
    onSetActiveNightIntervals?.(shiftId, updated);
    setAddingFor(null);
    setNewInizio('01:00');
    setNewFine('05:00');
  };

  const handleRemoveInterval = (shiftId, idx) => {
    const updated = (localIntervals[shiftId] || []).filter((_, i) => i !== idx);
    setLocalIntervals(prev => ({ ...prev, [shiftId]: updated }));
    onSetActiveNightIntervals?.(shiftId, updated);
  };

  if (!selectedDay) return null;

  const formattedDate = selectedDay.fullDate && selectedDay.fullDate.includes('-')
    ? selectedDay.fullDate.split('-').reverse().join('/')
    : selectedDay.fullDate;

  const renderNightPanel = (rs) => {
    if (!overlapsPassiveWindow(rs)) return null;
    const intervals = localIntervals[rs.id] || [];
    const isAdding = addingFor === rs.id;

    return (
      <Pane marginTop={6} paddingTop={6} borderTop="muted">
        <Pane display="flex" alignItems="center" gap={8} flexWrap="wrap">
          <Badge color="blue">Notte passiva 01:00–05:00</Badge>
          {intervals.map((iv, idx) => (
            <Pane key={idx} display="flex" alignItems="center" gap={4}>
              <Badge color="orange">Emergenza {iv.inizio}–{iv.fine}</Badge>
              {isDirector && (
                <IconButton
                  icon={TrashIcon}
                  size="small"
                  appearance="minimal"
                  intent="danger"
                  onClick={() => handleRemoveInterval(rs.id, idx)}
                />
              )}
            </Pane>
          ))}
          {isDirector && !isAdding && (
            <Button size="small" iconBefore={PlusIcon} onClick={() => {
              setAddingFor(rs.id);
              setNewInizio('01:00');
              setNewFine('05:00');
            }}>
              Aggiungi emergenza
            </Button>
          )}
        </Pane>

        {isAdding && (
          <Pane display="flex" alignItems="center" gap={8} marginTop={8} flexWrap="wrap">
            <TextInput
              width={80}
              placeholder="01:00"
              value={newInizio}
              onChange={e => setNewInizio(e.target.value)}
              onBlur={e => setNewInizio(normalizeTime(e.target.value))}
            />
            <Text>–</Text>
            <TextInput
              width={80}
              placeholder="05:00"
              value={newFine}
              onChange={e => setNewFine(e.target.value)}
              onBlur={e => setNewFine(normalizeTime(e.target.value))}
            />
            <Button size="small" appearance="primary" onClick={() => handleAddInterval(rs.id)}>Conferma</Button>
            <Button size="small" onClick={() => setAddingFor(null)}>Annulla</Button>
          </Pane>
        )}
      </Pane>
    );
  };

  const renderShifts = (typeLabel, shiftKey) => {
    let rawShifts = [];
    if (selectedDay.shifts.raw) {
      if (shiftKey === 'turno') {
        rawShifts = selectedDay.shifts.raw.filter(t => !['ferie', 'permesso', 'malattia'].includes(t.shift_type));
      } else {
        rawShifts = selectedDay.shifts.raw.filter(t => t.shift_type === shiftKey);
      }
    }

    return (
      <Pane background="tint2" padding={12} borderRadius={4}>
        <Heading size={500} color="muted">{typeLabel}</Heading>
        {rawShifts.length === 0 ? (
          <Text color="danger">Nessun turno pianificato</Text>
        ) : (
          rawShifts.map(rs => (
            <Pane key={rs.id} marginTop={8} paddingBottom={8} borderBottom="muted">
              <Pane display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={8}>
                <Pane display="flex" alignItems="center" gap={8} flexWrap="wrap">
                  <Text>{rs.operator_name}</Text>
                  <Text size={300} color="muted">({rs.start_time?.substring(0,5) || '?'} - {rs.end_time?.substring(0,5) || '?'})</Text>
                  {rs.shift_period === 'coda_della_notte' && <Text size={300} color="muted">[Coda della notte]</Text>}
                  {rs.shift_period === 'giorno'           && <Text size={300} color="muted">[Giorno]</Text>}
                  {rs.shift_period === 'notte'            && <Text size={300} color="muted">[Notte]</Text>}
                  {parseInt(rs.is_overtime, 10) === 1     && <Text size={300} color="warning">(Straordinario)</Text>}
                  {rs.status === 'timbrato'               && <Text size={300} color="success">[Timbrato]</Text>}
                </Pane>
                {isDirector && (
                  <IconButton icon={TrashIcon} intent="danger" appearance="minimal" onClick={() => onRemoveShift(rs.id)} />
                )}
              </Pane>
              {renderNightPanel(rs)}
            </Pane>
          ))
        )}
      </Pane>
    );
  };

  const renderFerie = () => {
    let rawShifts = [];
    if (selectedDay.shifts.raw) {
      rawShifts = selectedDay.shifts.raw.filter(t => ['ferie', 'permesso', 'malattia'].includes((t.shift_type || '').toLowerCase()));
    }
    if (rawShifts.length === 0) return null;

    return (
      <Pane background="redTint" padding={12} borderRadius={4} marginTop={8}>
        <Heading size={500} color="danger">Assenti (Ferie / Permessi / Malattia)</Heading>
        {rawShifts.map(rs => (
          <Pane key={rs.id} display="flex" alignItems="center" justifyContent="space-between" marginTop={4} paddingBottom={4} borderBottom="muted">
            <Text>{rs.operator_name}</Text>
            <Pane display="flex" alignItems="center" gap={8} flexWrap="wrap">
              {isDirector ? (
                <>
                  <Text size={300} color="muted">[{rs.shift_type.toUpperCase()}]</Text>
                  {['permesso', 'ferie'].includes((rs.shift_type || '').toLowerCase()) && rs.start_time && rs.start_time.substring(0,5) !== '00:00' && rs.end_time && (
                    <Text size={300} color="muted">({rs.start_time.substring(0,5)} - {rs.end_time.substring(0,5)})</Text>
                  )}
                </>
              ) : (
                <Text size={300} color="muted">[ASSENTE]</Text>
              )}
            </Pane>
          </Pane>
        ))}
      </Pane>
    );
  };

  return (
    <Dialog
      isShown={isOpen}
      title={`Dettaglio turni: ${formattedDate}`}
      onCloseComplete={onClose}
      hasFooter={false}
    >
      <Pane display="flex" flexDirection="column" gap={16}>
        {renderFerie()}
        {renderShifts('Turni pianificati', 'turno')}
        {isDirector && (
          <Pane display="flex" justifyContent="flex-end" gap={8} marginTop={24} paddingBottom={8}>
            <Button onClick={() => onAssignClick(selectedDay.fullDate)}>Inserisci turno per questo giorno</Button>
          </Pane>
        )}
      </Pane>
    </Dialog>
  );
}
